"use client";

import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import {
  BookingFlowLayout,
  BookingFlowLayoutMain,
  BookingFlowLayoutSidebar,
  Gallery,
  type GalleryImage,
  Badge,
  RequestToBookBadge,
} from "@fm/ui";
import { isRequestToBook } from "../lib/bookingRequestCopy";
import { VehicleInfoBar } from "./VehicleInfoBar";
import { VehicleDescription } from "./VehicleDescription";
import { VehicleHighlights } from "./VehicleHighlights";
import { VehicleRecommendedDestinations } from "./VehicleRecommendedDestinations";
import { VehicleSleepingArrangements } from "./VehicleSleepingArrangements";
import { VehicleRVDetails } from "./VehicleRVDetails";
import { VehicleAmenities } from "./VehicleAmenities";
import { VehicleRatesAndAvailability } from "./VehicleRatesAndAvailability";
import { VehicleAvailabilityCalendar } from "./VehicleAvailabilityCalendar";
import { ThingsYouShouldKnow } from "./ThingsYouShouldKnow";
import { VehicleLocation } from "./VehicleLocation";
import { BookingSummary } from "./BookingSummary";
import { AddonSelector } from "./AddonSelector";
import { InsuranceOptionSelector } from "./InsuranceOptionSelector";
import { VehicleReviews } from "./VehicleReviews";

import {
  vehicles,
  photos,
  videos,
  quotes,
  type ApiClient,
  type Vehicle,
  type VehicleVideo,
  type PricingBreakdown,
  type PricingRequest,
} from "@fm/api";
import { toDateString } from "@fm/ui";
import { EMPTY_LIST } from "../lib/stableDefaults";
import {
  extractErrorCode,
  getDeliveryErrorInfo,
  getDeliveryErrorMessage,
} from "../lib/deliveryErrors";
import { DeliveryErrorActions } from "./DeliveryErrorActions";
import {
  isCustomAddressOffered,
  isDeliveryOffered,
  resolveDeliveryMode,
} from "../lib/deliveryAvailability";
import { getApiBaseUrl } from "../lib/config";
import {
  parseSleepingArrangements,
  calculateAverageNightlyRate,
} from "../lib/vehicle-utils";
import { getStayDailyRates } from "../lib/stay-rates";
import { extractYoutubeVideoId } from "../lib/youtube";
import type {
  CheckoutSelections,
  DeliveryUrlState,
} from "../lib/checkout-selections";
import { calculateDeliveryFee } from "../lib/googleMaps";
import { useVehicleAddons } from "../hooks/useAddons";
import { keepOfferedAddonIds } from "../lib/offered-addon-ids";
import { useInsuranceOptions } from "../hooks/useInsuranceOptions";
import { useInsuranceOptionGroups } from "../hooks/useInsuranceOptionGroups";
import { useDeliveryLocations } from "../hooks/useDeliveryLocations";
import {
  useVehicleReviews,
  useVehicleReviewsStats,
  useMarkReviewHelpful,
} from "../hooks/useVehicleReviews";
import { useVehicleAvailabilityCalendar } from "../hooks/useVehicleAvailabilityCalendar";

interface BadgeItem {
  id: string;
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline" | "success";
}

const mockBadges: BadgeItem[] = [
  // Keeping mock badges for now as they might need specific logic or hardcoded values based on business rules
  { id: "1", label: "Instant Book", variant: "success" },
  { id: "2", label: "Delivery available", variant: "outline" },
];

export interface CustomerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

export interface CheckoutNavigationData {
  vehicle_id: string;
  from: string;
  to: string;
  adults: number;
  children: number;
  infants: number;
  pets: number;
}

export interface RVDetailContainerProps {
  // API Configuration
  apiKey: string;
  apiBaseUrl?: string;
  fleetId: string;
  vehicleId: string;

  // Search/Booking params
  searchParams: {
    from?: string;
    to?: string;
    adults: number;
    children: number;
    infants: number;
    pets: number;
  };

  vehicleName?: string;
  totalPrice?: number;

  // Section visibility. Defaults preserve current behavior for every consumer.
  showHighlights?: boolean;

  // Initial customer data (optional)
  initialCustomerData?: Partial<CustomerFormData>;
  initialDeliveryState?: DeliveryUrlState;
  initialAddonIds?: string[];
  initialInsuranceIds?: string[];

  // Callbacks
  onContinueToCheckout?: (selections: CheckoutSelections) => void;
  onBackToResults?: () => void;
  onImageClick?: (image: GalleryImage, index: number) => void;
  onCalendarDateRangeChange?: (
    from: string | undefined,
    to: string | undefined,
  ) => void;
  onDeliveryStateChange?: (state: DeliveryUrlState) => void;
  onSelectionsChange?: (addonIds: string[], insuranceIds: string[]) => void;

  // Theme
  theme?: "light" | "dark";
  primaryColor?: string;
}

function parseLocalDate(dateStr: string | undefined): Date | undefined {
  return dateStr ? new Date(dateStr + "T00:00:00") : undefined;
}

// Helper to construct address string
const getVehicleAddress = (vehicle: Vehicle): string => {
  if (vehicle.pickup_address) return vehicle.pickup_address;

  const components = [
    vehicle.pickup_city,
    vehicle.pickup_state,
    vehicle.pickup_zip_code,
  ].filter(Boolean);

  return components.length > 0 ? components.join(", ") : "United States";
};

function RVDetailSkeleton() {
  return (
    <div className="min-h-screen bg-white py-8 animate-pulse">
      <div className="booking-flow-layout">
        <div className="booking-flow-main">
          <div className="space-y-6">
            <div className="vehicle-gallery gallery-rounded-xl">
              <div className="gallery-scroll gallery-aspect-16-9 bg-gray-200" />
            </div>
            <div className="flex flex-wrap gap-2 mt-8">
              <div className="h-6 w-28 bg-gray-200 rounded-full" />
              <div className="h-6 w-20 bg-gray-200 rounded-full" />
              <div className="h-6 w-24 bg-gray-200 rounded-full" />
            </div>
            <div className="h-9 bg-gray-200 rounded w-3/5 mt-4" />
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 py-4 border-y border-gray-200">
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 bg-gray-200 rounded" />
                <div className="h-4 w-20 bg-gray-200 rounded" />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 bg-gray-200 rounded" />
                <div className="h-4 w-16 bg-gray-200 rounded" />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 bg-gray-200 rounded" />
                <div className="h-4 w-32 bg-gray-200 rounded" />
              </div>
            </div>
            <div className="mb-8">
              <div className="h-7 bg-gray-200 rounded w-1/4 mb-4" />
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded w-4/5" />
                <div className="h-4 bg-gray-200 rounded w-3/5" />
              </div>
            </div>
            <div className="mb-8">
              <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
              <div className="flex flex-wrap gap-2">
                <div className="h-7 w-28 bg-gray-200 rounded-full" />
                <div className="h-7 w-36 bg-gray-200 rounded-full" />
                <div className="h-7 w-24 bg-gray-200 rounded-full" />
                <div className="h-7 w-32 bg-gray-200 rounded-full" />
              </div>
            </div>
          </div>
        </div>
        <div className="booking-flow-sidebar">
          <div className="border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
            <div className="flex items-center gap-2">
              <div className="h-6 w-6 bg-gray-200 rounded" />
              <div className="h-9 w-24 bg-gray-200 rounded" />
              <div className="h-4 w-14 bg-gray-200 rounded" />
            </div>
            <div className="space-y-2">
              <div className="h-4 w-24 bg-gray-200 rounded" />
              <div className="h-10 bg-gray-200 rounded-lg" />
              <div className="space-y-1 pt-1">
                <div className="h-3 w-28 bg-gray-200 rounded" />
                <div className="h-3 w-28 bg-gray-200 rounded" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-4 w-20 bg-gray-200 rounded" />
              <div className="grid grid-cols-2 gap-3">
                <div className="h-[72px] bg-gray-200 rounded-lg" />
                <div className="h-[72px] bg-gray-200 rounded-lg" />
              </div>
            </div>
            <div className="h-px bg-gray-200" />
            <div className="space-y-3">
              <div className="flex justify-between">
                <div className="h-4 w-36 bg-gray-200 rounded" />
                <div className="h-4 w-16 bg-gray-200 rounded" />
              </div>
              <div className="flex justify-between">
                <div className="h-4 w-28 bg-gray-200 rounded" />
                <div className="h-4 w-14 bg-gray-200 rounded" />
              </div>
              <div className="h-4 w-24 bg-gray-200 rounded" />
              <div className="h-4 w-48 bg-gray-200 rounded" />
            </div>
            <div className="h-px bg-gray-200" />
            <div className="flex justify-between items-center">
              <div className="h-6 w-14 bg-gray-200 rounded" />
              <div className="h-7 w-20 bg-gray-200 rounded" />
            </div>
            <div className="space-y-3">
              <div className="h-12 bg-gray-200 rounded-md" />
              <div className="h-3 w-40 bg-gray-200 rounded mx-auto" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Shared effective options (see `deliveryOptions` below).
const PICKUP_OPTIONS = { delivery_type: "pickup" } as const;
const LOCATION_DELIVERY_OPTIONS = { delivery_type: "delivery" } as const;

export function RVDetailContainer({
  apiKey,
  apiBaseUrl,
  fleetId,
  vehicleId,
  searchParams,
  totalPrice = 0,
  showHighlights = true,
  initialCustomerData,
  initialDeliveryState,
  initialAddonIds,
  initialInsuranceIds,
  onContinueToCheckout,
  onBackToResults,
  onImageClick,
  onCalendarDateRangeChange,
  onDeliveryStateChange,
  onSelectionsChange,
}: RVDetailContainerProps) {
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [vehiclePhotos, setVehiclePhotos] = useState<GalleryImage[]>([]);
  const [vehicleVideos, setVehicleVideos] = useState<VehicleVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiClient = useMemo<ApiClient>(
    () => ({
      apiKey,
      baseUrl: getApiBaseUrl(apiBaseUrl),
    }),
    [apiKey, apiBaseUrl],
  );

  // Load add-ons
  const {
    data: addons = EMPTY_LIST,
    isLoading: isLoadingAddons,
    isSuccess: isAddonsLoaded,
  } = useVehicleAddons({
    apiKey,
    fleetId,
    vehicleId,
    apiBaseUrl,
  });

  // Load insurance options
  const {
    data: insuranceOptions = EMPTY_LIST,
    isLoading: isInsuranceLoading,
    isSuccess: isInsuranceLoaded,
  } = useInsuranceOptions({ apiKey, fleetId, vehicleId, apiBaseUrl });

  const { data: insuranceGroups = EMPTY_LIST } = useInsuranceOptionGroups({
    apiKey,
    fleetId,
    apiBaseUrl,
  });

  // Load delivery locations
  const {
    data: deliveryLocations = EMPTY_LIST,
    isLoading: isLoadingLocations,
    isError: locationsFailed,
    refetch: refetchLocations,
  } = useDeliveryLocations({
    apiKey,
    vehicleId,
    apiBaseUrl,
  });

  const { data: reviewsStats, isLoading: isStatsLoading } =
    useVehicleReviewsStats({
      vehicleId,
      fleetId,
      apiKey,
      apiBaseUrl,
      enabled: !!vehicleId,
    });

  const {
    data: reviewsData,
    isLoading: isReviewsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVehicleReviews({
    vehicleId,
    fleetId,
    apiKey,
    apiBaseUrl,
    enabled: !!vehicleId,
  });

  const markHelpfulMutation = useMarkReviewHelpful({
    vehicleId,
    fleetId,
    apiKey,
    apiBaseUrl,
  });

  // Load calendar availability data to calculate rates. Anchor the 2-month
  // window to the searched check-in date — otherwise the hook defaults to
  // "today" and never fetches the booked dates when they are further out,
  // leaving rates and the nightly price at 0.
  const { calendarData, unavailableRanges } = useVehicleAvailabilityCalendar({
    apiKey,
    vehicleId,
    fleetId,
    apiBaseUrl,
    baseDate: parseLocalDate(searchParams.from),
    monthsToFetch: 2,
    enabled: !!vehicleId && !!fleetId,
  });

  const calculatedRates = useMemo(() => {
    const weekdayRates: number[] = [];
    const weekendRates: number[] = [];

    calendarData.forEach((dayData, dateStr) => {
      if (dayData.rate !== null && dayData.available) {
        const date = new Date(dateStr + "T00:00:00");
        const dayOfWeek = date.getDay();
        // Friday (5) and Saturday (6) are weekends
        if (dayOfWeek === 5 || dayOfWeek === 6) {
          weekendRates.push(dayData.rate);
        } else {
          weekdayRates.push(dayData.rate);
        }
      }
    });

    const avgWeekday =
      weekdayRates.length > 0
        ? weekdayRates.reduce((a, b) => a + b, 0) / weekdayRates.length
        : 0;
    const avgWeekend =
      weekendRates.length > 0
        ? weekendRates.reduce((a, b) => a + b, 0) / weekendRates.length
        : 0;

    return {
      weekdayRate: avgWeekday,
      weekendRate: avgWeekend,
    };
  }, [calendarData]);

  const unavailableDates = useMemo(() => {
    const dates = new Set<string>();

    calendarData.forEach((dayData, dateStr) => {
      if (!dayData.available) {
        dates.add(dateStr);
      }
    });

    unavailableRanges.forEach((range) => {
      const start = new Date(range.start_date + "T00:00:00");
      const end = new Date(range.end_date + "T00:00:00");
      const current = new Date(start);
      while (current <= end) {
        dates.add(toDateString(current));
        current.setDate(current.getDate() + 1);
      }
    });

    return dates;
  }, [calendarData, unavailableRanges]);

  const [selectedDates, setSelectedDates] = useState<{
    from: string | undefined;
    to: string | undefined;
  }>({
    from: searchParams.from,
    to: searchParams.to,
  });

  const [requestedDeliveryOptions, setDeliveryOptions] = useState<{
    delivery_type: "pickup" | "delivery";
    address?: {
      address: string;
      city: string;
      state: string;
      zipCode: string;
      lat?: number;
      lng?: number;
    };
  }>(() => {
    if (
      initialDeliveryState?.delivery_type === "delivery" &&
      initialDeliveryState.delivery_option === "location" &&
      initialDeliveryState.delivery_location_id
    ) {
      return { delivery_type: "delivery" };
    }
    if (
      initialDeliveryState?.delivery_type === "delivery" &&
      initialDeliveryState.delivery_address
    ) {
      return {
        delivery_type: "delivery",
        address: {
          address: initialDeliveryState.delivery_address,
          city: initialDeliveryState.delivery_city || "",
          state: initialDeliveryState.delivery_state || "",
          zipCode: initialDeliveryState.delivery_zip || "",
          lat: initialDeliveryState.delivery_lat,
          lng: initialDeliveryState.delivery_lng,
        },
      };
    }
    return { delivery_type: "pickup" };
  });

  const [deliveryOption, setDeliveryOption] = useState<"custom" | "location">(
    initialDeliveryState?.delivery_type === "delivery" &&
      initialDeliveryState.delivery_option === "location"
      ? "location"
      : "custom",
  );
  const [selectedLocationId, setSelectedLocationId] = useState<string>(
    initialDeliveryState?.delivery_type === "delivery" &&
      initialDeliveryState.delivery_option === "location"
      ? (initialDeliveryState.delivery_location_id ?? "")
      : "",
  );

  // Delivery is offered only for a vehicle that says so; while it loads (or
  // when the flag is false/absent) the page is pickup-only. A vehicle that
  // takes no typed address delivers only to its assigned locations (and to
  // nowhere when it has none). Everything below reads the effective options,
  // so a preselected or stale delivery choice never reaches a pricing
  // request, the URL state or the checkout.
  const deliveryMode = resolveDeliveryMode(vehicle, deliveryLocations);
  const deliveryOffered = deliveryMode !== "none";
  const customAddressOffered = deliveryMode === "both";
  // The locations are waited for only when they can change the mode: a
  // vehicle with no typed addresses has only them, and a chosen location can
  // only be judged against them. Every other vehicle is priced right away.
  const locationsDecideMode =
    isDeliveryOffered(vehicle) &&
    (!isCustomAddressOffered(vehicle) ||
      (requestedDeliveryOptions.delivery_type === "delivery" &&
        deliveryOption === "location" &&
        !!selectedLocationId));
  const deliveryModeKnown =
    !!vehicle && (!isLoadingLocations || !locationsDecideMode);
  // Once the summary is shown it stays: it reads its initial delivery props
  // once, and remounting it would wipe an open dialog or a typed address.
  const [summaryShown, setSummaryShown] = useState(false);
  useEffect(() => {
    if (deliveryModeKnown) setSummaryShown(true);
  }, [deliveryModeKnown]);
  const showSummary = deliveryModeKnown || summaryShown;

  // A delivery carried in by the URL is honored only if the vehicle still
  // offers it: a typed address needs typed addresses, a location needs to be
  // one of the vehicle's locations.
  const initialIsLocation =
    initialDeliveryState?.delivery_option === "location";
  const initialDeliveryHonored =
    initialDeliveryState?.delivery_type === "delivery" &&
    (initialIsLocation
      ? deliveryOffered &&
        !!initialDeliveryState.delivery_location_id &&
        deliveryLocations.some(
          (location) =>
            location.id === initialDeliveryState.delivery_location_id,
        )
      : customAddressOffered);
  // Whether the chosen location is one of the vehicle's: a boolean, so the
  // locations arriving or refreshing cannot change the options' identity.
  const chosenLocationOffered = deliveryLocations.some(
    (location) => location.id === selectedLocationId,
  );
  // The effective options keep a stable identity while nothing they depend on
  // changes: a new object would re-fire pricing (and open another TaxCloud
  // cart) for a change that is not one.
  const deliveryOptions = useMemo<typeof requestedDeliveryOptions>(() => {
    if (
      !deliveryOffered ||
      requestedDeliveryOptions.delivery_type !== "delivery"
    )
      return PICKUP_OPTIONS;
    if (deliveryOption === "location") {
      // A location the vehicle does not have (stale selection) is no choice.
      return chosenLocationOffered ? LOCATION_DELIVERY_OPTIONS : PICKUP_OPTIONS;
    }
    return customAddressOffered ? requestedDeliveryOptions : PICKUP_OPTIONS;
  }, [
    deliveryOffered,
    customAddressOffered,
    requestedDeliveryOptions,
    deliveryOption,
    chosenLocationOffered,
  ]);

  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false);

  const [pricingBreakdown, setPricingBreakdown] =
    useState<PricingBreakdown | null>(null);
  const [isPricingLoading, setIsPricingLoading] = useState(false);
  // The code of a delivery refusal of the pricing request. Kept until the
  // renter chooses again, so a delivery that was switched off for them is
  // explained rather than silently gone.
  const [pricingRefusal, setPricingRefusal] = useState<string | null>(null);
  // What the vehicle or its locations were reloaded for already: a refusal
  // that persists after a reload must not reload again.
  const reloadedForRef = useRef(new Set<string>());
  const onRefusalRef = useRef<(code: string) => void>(() => {});

  // Add-ons management
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>(
    initialAddonIds ?? [],
  );

  // Insurance options management
  const [selectedInsuranceOptionIds, setSelectedInsuranceOptionIds] = useState<
    string[]
  >(initialInsuranceIds ?? []);

  // Enhanced delivery management
  const [addressSelected, setAddressSelected] = useState(
    initialDeliveryState?.delivery_type === "delivery" &&
      !!initialDeliveryState.delivery_address,
  );

  // TaxCloud cart ID persistence
  const taxcloudCartIdRef = useRef<string | null>(null);

  // Update effect to sync props to state if they change externally (e.g. initial load or nav)
  useEffect(() => {
    if (searchParams.from || searchParams.to) {
      setSelectedDates({ from: searchParams.from, to: searchParams.to });
    }
  }, [searchParams.from, searchParams.to]);

  // A pre-selected id can name an add-on this guest is not offered (a hidden
  // one smuggled in through a URL param). Prune once the list has loaded.
  useEffect(() => {
    if (!isAddonsLoaded) return;
    setSelectedAddonIds((prev) => keepOfferedAddonIds(prev, addons));
  }, [addons, isAddonsLoaded]);

  // Auto-select required add-ons
  useEffect(() => {
    const requiredIds = addons.filter((a) => a.required).map((a) => a.id);
    if (requiredIds.length > 0) {
      setSelectedAddonIds((prev) => [...new Set([...prev, ...requiredIds])]);
    }
  }, [addons]);

  // Auto-select required insurance options
  useEffect(() => {
    const requiredIds = (insuranceOptions || [])
      .filter((option) => option.required)
      .map((option) => option.id);
    if (requiredIds.length > 0) {
      setSelectedInsuranceOptionIds((prev) => [
        ...new Set([...prev, ...requiredIds]),
      ]);
    }
  }, [insuranceOptions]);

  // Drop a selected option the unit no longer offers
  useEffect(() => {
    if (!isInsuranceLoaded) return;
    const offered = new Set(insuranceOptions.map((option) => option.id));
    setSelectedInsuranceOptionIds((prev) =>
      prev.every((id) => offered.has(id))
        ? prev
        : prev.filter((id) => offered.has(id)),
    );
  }, [insuranceOptions, isInsuranceLoaded]);

  // Calculate delivery distance
  useEffect(() => {
    const calcDistance = async () => {
      if (
        deliveryOptions.delivery_type === "delivery" &&
        deliveryOptions.address?.lat &&
        deliveryOptions.address?.lng &&
        vehicle
      ) {
        setIsCalculatingDistance(true);
        try {
          // Ensure safe parsing of string/number values
          const baseFee = parseFloat(
            String(vehicle.delivery_minimum_fee || "0"),
          );
          const feePerMile = parseFloat(
            String(vehicle.delivery_fee_per_mile || "0"),
          );

          // Construct vehicle address string for origin
          const components = [
            vehicle.pickup_address,
            vehicle.pickup_city,
            vehicle.pickup_state,
            vehicle.pickup_zip_code,
          ].filter(Boolean);
          const origin = components.join(", ");

          const result = await calculateDeliveryFee(
            origin,
            {
              lat: deliveryOptions.address.lat,
              lng: deliveryOptions.address.lng,
            },
            baseFee,
            feePerMile,
          );
          setDeliveryDistance(result.distance);
        } catch (error) {
          console.error("Failed to calculate delivery distance:", error);
          setDeliveryDistance(null);
        } finally {
          setIsCalculatingDistance(false);
        }
      } else if (deliveryOptions.delivery_type !== "delivery") {
        setDeliveryDistance(null);
      }
    };

    calcDistance();
  }, [deliveryOptions, vehicle]);

  // Fetch Pricing Logic
  useEffect(() => {
    // Set by the cleanup when a newer run supersedes this one: an older
    // response (quote or refusal) must not overwrite the newer state.
    let cancelled = false;
    const fetchPricing = async () => {
      setIsPricingLoading(false);
      if (!vehicleId || !fleetId || !selectedDates.from || !selectedDates.to) {
        setPricingBreakdown(null);
        return;
      }

      // Whether delivery is offered decides what to price, so wait for the
      // vehicle instead of quoting a throwaway pickup that would race the
      // real quote.
      if (!vehicle) return;
      // Same for the vehicle's assigned locations, when they can change the
      // mode (see `locationsDecideMode`).
      if (!deliveryModeKnown) return;

      // Wait for distance calculation if delivery is selected
      if (isCalculatingDistance) return;

      // Validate delivery address if delivery is selected
      const pricesLocation =
        deliveryOptions.delivery_type === "delivery" &&
        deliveryOption === "location" &&
        !!selectedLocationId;
      if (!pricesLocation && deliveryOptions.delivery_type === "delivery") {
        if (!deliveryOptions.address || !addressSelected) {
          setPricingBreakdown(null);
          return;
        }

        // Ensure delivery distance is calculated (required by backend)
        if (deliveryDistance === null || deliveryDistance === undefined) {
          // Distance not yet calculated, skip pricing request
          setPricingBreakdown(null);
          return;
        }

        // Validate delivery distance against max radius
        if (
          vehicle?.max_delivery_radius_miles &&
          deliveryDistance > Number(vehicle.max_delivery_radius_miles)
        ) {
          console.error(
            `Delivery distance (${deliveryDistance.toFixed(2)} miles) exceeds maximum radius (${vehicle.max_delivery_radius_miles} miles)`,
          );
          setPricingBreakdown(null);
          return;
        }
      }

      setIsPricingLoading(true);
      try {
        const request: PricingRequest = {
          quotable_type: "fleet_vehicle",
          quotable_id: vehicleId,
          pick_up: selectedDates.from,
          return: selectedDates.to,
          selected_addon_ids: selectedAddonIds,
          selected_insurance_option_ids: selectedInsuranceOptionIds,
          taxcloud_cart_id: taxcloudCartIdRef.current || undefined,
        };

        // Add delivery options based on delivery type
        if (pricesLocation) {
          (request as any).delivery_options = {
            delivery_type: "delivery",
            address_type: "predefined",
            delivery_location_id: selectedLocationId,
          };
        } else if (
          deliveryOptions.delivery_type === "delivery" &&
          deliveryOptions.address
        ) {
          (request as any).delivery_options = {
            delivery_type: "delivery",
            address_type: "custom",
            delivery_distance: deliveryDistance || undefined,
            custom_address: {
              address: deliveryOptions.address.address,
              city: deliveryOptions.address.city,
              state: deliveryOptions.address.state,
              zip_code: deliveryOptions.address.zipCode,
              country: "US",
            },
          };
        } else {
          (request as any).delivery_options = {
            delivery_type: "pickup",
          };
        }

        const response = await quotes.calculatePricing(
          apiClient,
          fleetId,
          request,
        );
        // The cart id is kept even from a superseded quote, so the next one
        // does not open another cart.
        if (response.data?.taxcloud_cart_id) {
          taxcloudCartIdRef.current = response.data.taxcloud_cart_id;
        }
        if (cancelled) return;
        if (response.data) {
          setPricingBreakdown(response.data);
        }
      } catch (err: any) {
        if (cancelled) return;
        // Log the raw error first
        console.error("RAW ERROR:", err);

        const refusalCode = extractErrorCode(err);
        if (getDeliveryErrorInfo(refusalCode) && refusalCode) {
          setPricingRefusal(refusalCode);
          onRefusalRef.current(refusalCode);
        }

        // Log detailed error information
        console.error("Failed to calculate pricing:", {
          errorType: err?.constructor?.name,
          errorMessage: err?.message,
          errorStatus: err?.status,
          errorData: err?.data,
          errorDetails: err?.details,
          errorString: String(err),
          errorJSON: JSON.stringify(err, Object.getOwnPropertyNames(err)),
          request: {
            vehicleId,
            dates: { from: selectedDates.from, to: selectedDates.to },
            delivery: deliveryOptions.delivery_type,
            deliveryAddress: deliveryOptions.address,
            deliveryDistance,
            addons: selectedAddonIds,
            insurance: selectedInsuranceOptionIds,
          },
        });
        setPricingBreakdown(null);
      } finally {
        if (!cancelled) setIsPricingLoading(false);
      }
    };

    fetchPricing();
    return () => {
      cancelled = true;
    };
  }, [
    apiClient,
    fleetId,
    vehicleId,
    selectedDates.from,
    selectedDates.to,
    deliveryOptions,
    deliveryDistance,
    isCalculatingDistance,
    selectedAddonIds,
    selectedInsuranceOptionIds,
    addressSelected,
    deliveryOption,
    selectedLocationId,
    vehicle,
    deliveryModeKnown,
  ]);

  // Real per-night amounts of the selected stay. Null until the breakdown for
  // exactly these dates is loaded (the previous range's breakdown is kept in
  // state while the next one is fetched and must not be shown).
  const stayDailyRates = useMemo(
    () =>
      getStayDailyRates(pricingBreakdown, selectedDates.from, selectedDates.to),
    [pricingBreakdown, selectedDates.from, selectedDates.to],
  );

  const handleDateChange = (dates: [Date, Date] | null) => {
    // A refusal belonged to the previous choice.
    setPricingRefusal(null);
    if (dates) {
      const from = toDateString(dates[0]);
      const to = toDateString(dates[1]);
      setSelectedDates({ from, to });
      onCalendarDateRangeChange?.(from, to);
    } else {
      setSelectedDates({ from: undefined, to: undefined });
    }
  };

  const handleDeliveryChange = (
    type: "pickup" | "delivery",
    address?: {
      address: string;
      city: string;
      state: string;
      zipCode: string;
      lat?: number;
      lng?: number;
    },
  ) => {
    setPricingRefusal(null);
    if (type === "delivery" && !customAddressOffered) return;
    setDeliveryOptions({ delivery_type: type, address });
    if (type === "delivery" && address) {
      setDeliveryOption("custom");
      setAddressSelected(true);
      onDeliveryStateChange?.({
        delivery_type: "delivery",
        delivery_address: address.address,
        delivery_city: address.city,
        delivery_state: address.state,
        delivery_zip: address.zipCode,
        delivery_lat: address.lat,
        delivery_lng: address.lng,
        delivery_option: "custom",
      });
    } else if (type === "pickup") {
      setAddressSelected(false);
      setDeliveryDistance(null);
      onDeliveryStateChange?.({ delivery_type: "pickup" });
    }
  };

  const handleDeliveryLocationSelect = (locationId: string) => {
    setPricingRefusal(null);
    if (!deliveryOffered) return;
    // A fixed location replaces any custom address: delivery is on, with no
    // address or distance to carry along.
    setDeliveryOptions({ delivery_type: "delivery" });
    setAddressSelected(false);
    setDeliveryDistance(null);
    setDeliveryOption("location");
    setSelectedLocationId(locationId);
    onDeliveryStateChange?.({
      delivery_type: "delivery",
      delivery_option: "location",
      delivery_location_id: locationId,
    });
  };

  // The server refused a delivery the page was offering: what the page knows
  // is stale, so reload it (once per kind of refusal) and let the UI follow.
  onRefusalRef.current = (code: string) => {
    if (reloadedForRef.current.has(code)) return;
    reloadedForRef.current.add(code);
    if (code === "delivery_location_not_found") {
      void refetchLocations();
    } else if (
      code === "delivery_not_available" ||
      code === "custom_address_not_available"
    ) {
      vehicles
        .get(apiClient, fleetId, vehicleId)
        .then((response) => {
          if (response.data) setVehicle(response.data);
        })
        .catch(() => {
          // The refusal message already tells the renter what to do.
        });
    }
  };

  const handleAddonIdsChange = (ids: string[]) => {
    // A refusal belonged to the previous choice.
    setPricingRefusal(null);
    setSelectedAddonIds(ids);
    onSelectionsChange?.(ids, selectedInsuranceOptionIds);
  };

  const handleInsuranceIdsChange = (ids: string[]) => {
    // A refusal belonged to the previous choice.
    setPricingRefusal(null);
    setSelectedInsuranceOptionIds(ids);
    onSelectionsChange?.(selectedAddonIds, ids);
  };

  const handleCheckout = () => {
    if (!vehicle || !selectedDates.from || !selectedDates.to) return;

    const selections: CheckoutSelections = {
      selectedAddonIds,
      selectedInsuranceOptionIds,
      deliveryType: deliveryOptions.delivery_type,
      deliveryOption: customAddressOffered ? deliveryOption : "location",
      selectedLocationId:
        deliveryOptions.delivery_type === "delivery" &&
        deliveryOption === "location"
          ? selectedLocationId
          : "",
      customAddress: deliveryOptions.address
        ? {
            address: deliveryOptions.address.address,
            city: deliveryOptions.address.city,
            state: deliveryOptions.address.state,
            zipCode: deliveryOptions.address.zipCode,
            country: "US",
            lat: deliveryOptions.address.lat ?? 0,
            lng: deliveryOptions.address.lng ?? 0,
          }
        : null,
      deliveryDistance,
      taxcloudCartId: taxcloudCartIdRef.current,
    };

    onContinueToCheckout?.(selections);
  };

  // A delivery carried in by the URL for a vehicle that does not offer it:
  // tell the parent to drop it, so it is not passed along again.
  useEffect(() => {
    if (
      deliveryModeKnown &&
      // A failed request says nothing about the locations: keep the URL, but
      // only when they decide the mode.
      !(locationsFailed && locationsDecideMode) &&
      initialDeliveryState?.delivery_type === "delivery" &&
      !initialDeliveryHonored
    ) {
      onDeliveryStateChange?.({ delivery_type: "pickup" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deliveryModeKnown,
    locationsFailed,
    locationsDecideMode,
    initialDeliveryHonored,
    initialDeliveryState?.delivery_type,
  ]);

  useEffect(() => {
    const loadData = async () => {
      if (!vehicleId || !fleetId) return;

      try {
        setIsLoading(true);
        setError(null);

        const [vehicleResponse, photosResponse, videosResponse] =
          await Promise.all([
            vehicles.get(apiClient, fleetId, vehicleId),
            photos.list(apiClient, vehicleId),
            videos.list(apiClient, vehicleId),
          ]);

        if (vehicleResponse.data) {
          setVehicle(vehicleResponse.data);
        }

        if (photosResponse.data) {
          const galleryImages: GalleryImage[] = photosResponse.data.map(
            (photo) => ({
              id: photo.id,
              url: photo.attachment_urls.original,
              alt: photo.name || "Vehicle photo",
            }),
          );
          setVehiclePhotos(galleryImages);
        }

        if (videosResponse.data) {
          setVehicleVideos(
            videosResponse.data.filter((video) => video.status === "ready"),
          );
        }
      } catch (err) {
        console.error("Failed to load vehicle data:", err);
        setError("Failed to load vehicle details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [apiClient, fleetId, vehicleId]);

  if (isLoading) {
    return <RVDetailSkeleton />;
  }

  if (error || !vehicle) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600">{error || "Vehicle not found"}</p>
          {onBackToResults && (
            <button
              onClick={onBackToResults}
              className="mt-4 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
            >
              Back to Results
            </button>
          )}
        </div>
      </div>
    );
  }

  const allAmenities = [
    ...(vehicle.kitchen_amenities || []),
    ...(vehicle.bathroom_amenities || []),
    ...(vehicle.entertainment_amenities || []),
    ...(vehicle.climate_control_amenities || []),
    ...(vehicle.outdoor_amenities || []),
    ...(vehicle.safety_amenities || []),
    ...(vehicle.other_amenities || []),
  ];

  const bedCounts = parseSleepingArrangements(vehicle.description);

  const sleepingArrangements = {
    sleeps: vehicle.sleeps || 0,
    ...bedCounts,
  };

  const rvDetails = {
    year: vehicle.year || new Date().getFullYear(),
    manufacturer: vehicle.make || "Unknown",
    model: vehicle.model || "Unknown",
    // The RV class label, else the vehicle type label; never the raw
    // `category` slug.
    equipmentType:
      vehicle.rv_class?.label || vehicle.vehicle_type?.label || "RV",
    sleeps: vehicle.sleeps || 0,
    length: vehicle.length_feet ? `${vehicle.length_feet} ft` : "N/A",
  };

  const rates = {
    ...calculatedRates,
    minStay: vehicle.minimum_rental_nights || 1,
    maxStay: vehicle.maximum_rental_nights || 30,
  };

  const vehicleSpecs = {
    sleeps: vehicle.sleeps || 0,
    length: vehicle.length_feet ? `${vehicle.length_feet} ft` : "N/A",
    location: `${vehicle.pickup_city || ""}, ${vehicle.pickup_state || ""}`,
  };

  const badges = [...mockBadges];
  if (vehicle.pickup_city) {
    badges.unshift({
      id: "loc",
      label: `${vehicle.pickup_city}, ${vehicle.pickup_state}`,
      variant: "secondary",
    });
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <BookingFlowLayout>
        <BookingFlowLayoutMain>
          <div className="space-y-6">
            {/* Gallery */}
            <Gallery
              images={vehiclePhotos}
              aspectRatio="16/9"
              rounded="xl"
              showNavigation
              showIndicators
              onImageClick={onImageClick}
            />
            {/* Videos */}
            {vehicleVideos.length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4">Videos</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {vehicleVideos.map((video) => {
                    if (video.source === "youtube") {
                      const youtubeId = video.external_url
                        ? extractYoutubeVideoId(video.external_url)
                        : null;
                      // A malformed external_url slipped through somehow:
                      // skip it rather than render a broken iframe.
                      if (!youtubeId) return null;

                      return (
                        <iframe
                          key={video.id}
                          src={`https://www.youtube.com/embed/${youtubeId}`}
                          title={video.name || "YouTube video"}
                          className="w-full rounded-xl aspect-video"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          frameBorder={0}
                        />
                      );
                    }

                    return (
                      <video
                        key={video.id}
                        controls
                        poster={video.thumbnail_url || undefined}
                        src={video.attachment_url || undefined}
                        className="w-full rounded-xl aspect-video"
                      />
                    );
                  })}
                </div>
              </div>
            )}
            {/* Badges */}
            <div className="flex flex-wrap gap-2 mt-8">
              {badges.map((badge) => (
                <Badge key={badge.id} variant={badge.variant}>
                  {badge.label}
                </Badge>
              ))}
            </div>
            {/* Vehicle Title */}
            <h1 className="text-3xl font-bold mt-4 mb-6">
              {vehicle.headline?.trim() || vehicle.name}
            </h1>
            {isRequestToBook(vehicle) && (
              <RequestToBookBadge className="-mt-3 mb-6" />
            )}
            {/* Vehicle Info Bar */}
            <VehicleInfoBar
              sleeps={vehicleSpecs.sleeps}
              length={vehicleSpecs.length}
              location={vehicleSpecs.location}
            />
            {/* Description */}
            <VehicleDescription
              description={vehicle.description || "No description available."}
            />
            {/* Highlights — hidden via showHighlights prop (temporarily off in embed/rv). */}
            {showHighlights && (
              <VehicleHighlights
                highlights={
                  vehicle.highlights
                    ? [vehicle.highlights]
                    : ["Bedding included", "Kitchen essentials"]
                }
              />
            )}

            {/* Recommended Destinations */}
            {vehicle.recommend_destinations && (
              <VehicleRecommendedDestinations
                destinations={vehicle.recommend_destinations}
              />
            )}

            {/* Sleeping Arrangements */}
            <VehicleSleepingArrangements {...sleepingArrangements} />

            {/* RV Details */}
            <VehicleRVDetails {...rvDetails} />

            {/* Amenities */}
            <VehicleAmenities amenities={allAmenities} />

            {/* Rates & Availability - Showing basic info */}
            <VehicleRatesAndAvailability {...rates} />

            {/* Availability Calendar */}
            <VehicleAvailabilityCalendar
              apiKey={apiKey}
              vehicleId={vehicleId}
              fleetId={fleetId}
              apiBaseUrl={apiBaseUrl}
              initialFrom={selectedDates.from}
              initialTo={selectedDates.to}
              onDateRangeChange={(from, to) => {
                setSelectedDates({ from, to });
                onCalendarDateRangeChange?.(from, to);
              }}
              minNights={vehicle.minimum_rental_nights || 1}
              maxNights={vehicle.maximum_rental_nights || 30}
              stayDailyRates={stayDailyRates}
            />

            {/* Things You Should Know */}
            <ThingsYouShouldKnow
              petsAllowed={vehicle.pets_allowed}
              smokingAllowed={vehicle.smoking_allowed}
              festivalsEventsAllowed={vehicle.festivals_events_allowed}
              pickupTime={vehicle.pickup_time}
              dropoffTime={vehicle.dropoff_time}
            />

            {/* RV Location */}
            <VehicleLocation
              apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || ""}
              address={getVehicleAddress(vehicle)}
            />

            {/* Insurance Options */}
            {insuranceOptions && insuranceOptions.length > 0 && (
              <div className="bg-white rounded-xl">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  Insurance & Protection
                </h2>
                <InsuranceOptionSelector
                  options={insuranceOptions}
                  groups={insuranceGroups}
                  selectedIds={selectedInsuranceOptionIds}
                  onChange={handleInsuranceIdsChange}
                  isLoading={isInsuranceLoading}
                />
              </div>
            )}

            {/* Add-ons & Extras */}
            {addons && addons.length > 0 && (
              <Fragment>
                <div className="border-t border-gray-200 my-8"></div>

                <div className="bg-white rounded-xl">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Add-ons & Extras
                  </h2>
                  <AddonSelector
                    addons={addons}
                    selectedAddonIds={selectedAddonIds}
                    onSelectionChange={handleAddonIdsChange}
                    isLoading={isLoadingAddons}
                  />
                </div>
              </Fragment>
            )}
            <div className="border-t border-gray-200 my-8"></div>
            <VehicleReviews
              reviews={reviewsData?.reviews || []}
              averageRating={reviewsStats?.average_rating ?? undefined}
              totalReviews={reviewsStats?.total_reviews}
              isLoading={isReviewsLoading}
              isStatsLoading={isStatsLoading}
              hasNextPage={hasNextPage}
              onLoadMore={() => fetchNextPage()}
              isLoadingMore={isFetchingNextPage}
              onMarkHelpful={(reviewId) => markHelpfulMutation.mutate(reviewId)}
              markingHelpfulReviewId={
                markHelpfulMutation.isPending
                  ? (markHelpfulMutation.variables ?? null)
                  : null
              }
            />
          </div>
        </BookingFlowLayoutMain>

        <BookingFlowLayoutSidebar>
          {pricingRefusal && getDeliveryErrorInfo(pricingRefusal) && (
            <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
              <p>{getDeliveryErrorMessage(pricingRefusal, false)}</p>
              <DeliveryErrorActions
                code={pricingRefusal}
                hasLocations={false}
                onChoosePickup={() => handleDeliveryChange("pickup")}
                onChooseLocation={() => handleDeliveryChange("pickup")}
              />
            </div>
          )}
          {!showSummary ? (
            <div className="h-64 animate-pulse rounded-xl bg-gray-100" />
          ) : (
            <BookingSummary
              vehicleName={vehicle.name}
              pricePerNight={
                // Grid rate of the selected dates (BookingSummary swaps it for
                // the real stay rate once the breakdown is loaded); falls back
                // to the searched dates when nothing is selected.
                calculateAverageNightlyRate(
                  calendarData,
                  selectedDates.from,
                  selectedDates.to,
                ) ||
                calculateAverageNightlyRate(
                  calendarData,
                  searchParams.from,
                  searchParams.to,
                )
              }
              minStay={vehicle.minimum_rental_nights || 1}
              maxStay={vehicle.maximum_rental_nights || 30}
              unavailableDates={unavailableDates}
              checkIn={parseLocalDate(selectedDates.from)}
              checkOut={parseLocalDate(selectedDates.to)}
              onContinue={handleCheckout}
              onDatesChange={handleDateChange}
              onDeliveryChange={handleDeliveryChange}
              deliveryAvailable={deliveryOffered}
              deliveryType={deliveryOptions.delivery_type}
              customAddressAvailable={customAddressOffered}
              deliveryLocations={deliveryLocations}
              onDeliveryLocationSelect={handleDeliveryLocationSelect}
              requestToBook={isRequestToBook(vehicle)}
              pricingBreakdown={pricingBreakdown}
              isLoadingPricing={isPricingLoading}
              deliveryBaseFee={
                vehicle.delivery_minimum_fee
                  ? parseFloat(String(vehicle.delivery_minimum_fee))
                  : undefined
              }
              deliveryFeePerMile={
                vehicle.delivery_fee_per_mile
                  ? parseFloat(String(vehicle.delivery_fee_per_mile))
                  : undefined
              }
              deliveryMaxRadius={
                vehicle.max_delivery_radius_miles
                  ? parseFloat(String(vehicle.max_delivery_radius_miles))
                  : undefined
              }
              addons={addons}
              selectedAddonIds={selectedAddonIds}
              onAddonsChange={handleAddonIdsChange}
              isLoadingAddons={isLoadingAddons}
              insuranceOptions={insuranceOptions}
              insuranceGroups={insuranceGroups}
              selectedInsuranceOptionIds={selectedInsuranceOptionIds}
              onInsuranceOptionsChange={handleInsuranceIdsChange}
              isLoadingInsurance={isInsuranceLoading}
              pickupLocation={
                [vehicle.pickup_city, vehicle.pickup_state]
                  .filter(Boolean)
                  .join(", ") || undefined
              }
              initialRentalType={
                initialDeliveryHonored ? "delivery" : undefined
              }
              initialDeliveryOption={
                initialDeliveryHonored && initialIsLocation
                  ? "location"
                  : undefined
              }
              initialDeliveryLocationId={
                initialDeliveryHonored && initialIsLocation
                  ? initialDeliveryState?.delivery_location_id
                  : undefined
              }
              initialDeliveryAddress={
                initialDeliveryHonored &&
                !initialIsLocation &&
                initialDeliveryState?.delivery_address
                  ? {
                      address: initialDeliveryState.delivery_address,
                      city: initialDeliveryState.delivery_city || "",
                      state: initialDeliveryState.delivery_state || "",
                      zipCode: initialDeliveryState.delivery_zip || "",
                      lat: initialDeliveryState.delivery_lat,
                      lng: initialDeliveryState.delivery_lng,
                    }
                  : undefined
              }
            />
          )}
        </BookingFlowLayoutSidebar>
      </BookingFlowLayout>
    </div>
  );
}
